import { IAnalyticsProvider } from './analytics-provider.interface';

type ClarityFn = (...args: unknown[]) => void;

export class ClarityAnalyticsProvider implements IAnalyticsProvider {
	initialize(projectId: string): void {
		const script = document.createElement('script');
		script.type = 'text/javascript';
		script.innerHTML = `
			(function(c,l,a,r,i,t,y){
				c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
				t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
				y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
			})(window, document, "clarity", "script", "${projectId}");
		`;
		document.head.appendChild(script);
	}

	identifyUser(userId: string, role: string, friendlyName?: string, pageId?: string): void {
		const clarity = this._clarity();
		if (!clarity) {
			return;
		}

		// identify(custom-id, custom-session-id, custom-page-id, friendly-name).
		// Clarity hashes the custom-id and never shows it as plain text — pass a friendly-name so a
		// readable value renders on the dashboard, and expose the raw id as a filterable custom tag
		// (the hashed id is not a tag). custom-page-id is a normalized route pattern so pages with
		// embedded IDs group together in the Pages view, heatmaps, and filters.
		clarity('identify', userId, undefined, pageId, friendlyName ?? userId);
		clarity('set', 'userId', userId);
		clarity('set', 'role', role);
	}

	setTag(key: string, value: string | string[]): void {
		this._clarity()?.('set', key, value);
	}

	trackEvent(eventName: string, properties?: Record<string, unknown>): void {
		const clarity = this._clarity();
		if (!clarity) {
			return;
		}

		// Clarity custom events carry a name only — fan any properties out into
		// custom tags so the event's context is filterable on the session.
		if (properties) {
			for (const [key, value] of Object.entries(properties)) {
				if (value !== undefined && value !== null) {
					clarity('set', key, String(value));
				}
			}
		}

		clarity('event', eventName);
	}

	private _clarity(): ClarityFn | null {
		const clarity = (window as unknown as Record<string, unknown>)['clarity'];
		return typeof clarity === 'function' ? (clarity as ClarityFn) : null;
	}
}
